import { useState, useEffect } from "react";
import { Truck, Package, MapPin, Eye, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["placed", "confirmed", "packed", "shipped", "delivered", "cancelled"];
const STATUS_COLORS = { placed: "bg-blue-100 text-blue-700", confirmed: "bg-cyan-100 text-cyan-700", packed: "bg-indigo-100 text-indigo-700", shipped: "bg-amber-100 text-amber-700", delivered: "bg-emerald-100 text-emerald-700", cancelled: "bg-red-100 text-red-700" };

export default function AdminShipments() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("shipped");
  const [selected, setSelected] = useState(null);
  const [trackNum, setTrackNum] = useState("");
  const [newStatus, setNewStatus] = useState("");

  const fetch = () => {
    setLoading(true);
    axios.get(`${API}/admin/orders?status=${filter}&limit=50`, { withCredentials: true }).then(r => setOrders(r.data.orders || [])).finally(() => setLoading(false));
  };
  useEffect(() => { fetch(); }, [filter]);

  const updateShipment = async (oid) => {
    try {
      await axios.put(`${API}/admin/orders/${oid}/status`, { status: newStatus, tracking_number: trackNum }, { withCredentials: true });
      toast.success("Shipment updated");
      setSelected(null);
      fetch();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-shipments-page">
      <h1 className="text-2xl font-bold text-slate-800 mb-6">Shipments</h1>
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[["packed", "Packed", "indigo"], ["shipped", "Shipped", "amber"], ["delivered", "Delivered", "emerald"], ["cancelled", "Cancelled", "red"]].map(([s, l, c]) => (
          <button key={s} onClick={() => setFilter(s)} className={`bg-white rounded-2xl p-4 shadow-sm border text-left hover:shadow-md transition-all ${filter === s ? 'ring-2 ring-cyan-500' : ''}`}>
            <Truck className={`w-5 h-5 text-${c}-600 mb-2`} /><p className="text-sm font-semibold text-slate-800 capitalize">{l}</p><p className="text-xs text-slate-500">Click to filter</p>
          </button>
        ))}
      </div>
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-500 border-b bg-slate-50"><th className="p-4">Order ID</th><th className="p-4">Customer</th><th className="p-4">Items</th><th className="p-4">Tracking</th><th className="p-4">Status</th><th className="p-4">Date</th><th className="p-4">Action</th></tr></thead>
          <tbody>
            {loading ? [...Array(3)].map((_, i) => <tr key={i}><td colSpan={7} className="p-4"><div className="h-10 skeleton-loading rounded" /></td></tr>) :
            orders.map(o => (
              <tr key={o.order_id} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="p-4 font-medium">{o.order_id}</td>
                <td className="p-4">{o.user_name}</td>
                <td className="p-4">{o.items?.length} items</td>
                <td className="p-4 text-xs font-mono">{o.tracking_number || <span className="text-slate-400">Not assigned</span>}</td>
                <td className="p-4"><Badge className={`text-xs capitalize ${STATUS_COLORS[o.status]}`}>{o.status}</Badge></td>
                <td className="p-4 text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString()}</td>
                <td className="p-4"><button onClick={() => { setSelected(o); setNewStatus(o.status); setTrackNum(o.tracking_number || ""); }} className="p-1.5 hover:bg-cyan-50 rounded-lg text-slate-500 hover:text-cyan-600"><Eye className="w-4 h-4" /></button></td>
              </tr>
            ))}
            {!loading && orders.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">No shipments found</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Update Shipment: {selected?.order_id}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div><label className="text-xs font-medium">Tracking Number</label><Input value={trackNum} onChange={e => setTrackNum(e.target.value)} className="rounded-xl mt-1" placeholder="Enter tracking number" /></div>
              <div><label className="text-xs font-medium">Status</label><Select value={newStatus} onValueChange={setNewStatus}><SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent></Select></div>
              <Button onClick={() => updateShipment(selected.order_id)} className="w-full rounded-full bg-cyan-600 hover:bg-cyan-700">Update Shipment</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
